import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { customerService } from "../../services/customerService";
import { CustomerServiceDto } from "../../models/Service";

export interface CustomerServicesState {
  customerServices: CustomerServiceDto;
  errors: string | null;
  loading: boolean;
}

const initialState: CustomerServicesState = {
  customerServices: {
    subscribedServices: [],
    unsubscribedServices: [],
  },
  errors: null,
  loading: false,
};

// Thunk to fetch all customer services
export const getAllCustomerServices = createAsyncThunk<CustomerServiceDto>(
  "customerService/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await customerService.getCustomerServices();
      return res.data; // Ensure this returns CustomerServiceDto
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch services";
      return rejectWithValue(errorMessage);
    }
  }
);

// Thunk to activate a service
export const activateService = createAsyncThunk<
  any,
  { customerId: string; partnerServiceId: string }
>(
  "customerService/activate",
  async ({ customerId, partnerServiceId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await customerService.ActivateService(
        partnerServiceId,
        customerId
      );
      dispatch(getAllCustomerServices()); // Refresh services after activation
      return res;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to activate service";
      return rejectWithValue(errorMessage);
    }
  }
);

// Thunk to deactivate a service
export const deactivateService = createAsyncThunk<
  any,
  { partnerServiceId: string }
>(
  "customerService/deactivate",
  async ({ partnerServiceId }, { rejectWithValue, dispatch }) => {
    try {
      const res = await customerService.DeactivateService(partnerServiceId);
      dispatch(getAllCustomerServices()); // Refresh services after deactivation
      return res;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to deactivate service";
      return rejectWithValue(errorMessage);
    }
  }
);

const customerServicesSlice = createSlice({
  name: "customerServices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllCustomerServices.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getAllCustomerServices.fulfilled, (state, action) => {
        state.customerServices = action.payload; // Update with fetched services
        state.loading = false;
        state.errors = null;
      })
      .addCase(getAllCustomerServices.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(activateService.rejected, (state, action) => {
        state.errors = action.payload as string;
      })
      .addCase(deactivateService.rejected, (state, action) => {
        state.errors = action.payload as string;
      });
  },
});

 export default customerServicesSlice.reducer;
